import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { EditorialNav } from '../components/editorial/EditorialNav';
import { EditorialFooter } from '../components/editorial/EditorialFooter';

const PARTNERSHIP_CTA = 'https://calendar.app.google/rM2Fom2dLoA8PVLG6';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: [0.16, 1, 0.3, 1] } }
};

const Organizations = () => {
  return (
    <div className="bg-[#f4efe6] text-[#141416] min-h-screen font-sans-clean overflow-x-hidden">
      <EditorialNav />

      {/* HERO — editorial asymmetric, B2B-clean */}
      <section className="relative pt-36 pb-20 md:pt-44 md:pb-32 px-6 md:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-12">
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { delayChildren: 0.1, staggerChildren: 0.12 } } }}
            className="col-span-12 md:col-span-10 md:col-start-2 lg:col-span-9 lg:col-start-2"
          >
            <motion.div variants={fadeUp} className="flex items-center gap-3 mb-10">
              <div className="h-px w-10 md:w-14 bg-[#c6a04a]" />
              <p className="font-sans-clean text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#c6a04a]">
                For Organizations
              </p>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="font-editorial text-[2.4rem] sm:text-6xl md:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-[-0.035em] text-[#141416] mb-10"
              style={{ fontVariationSettings: '"opsz" 144, "SOFT" 30, "wght" 380' }}
            >
              A healthier,<br />
              <span className="font-editorial-italic text-[#c6a04a]" style={{ fontVariationSettings: '"opsz" 144, "SOFT" 70' }}>higher-output</span> team.
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="font-sans-clean text-base md:text-lg text-[#141416]/70 max-w-2xl leading-[1.7] mb-12"
            >
              CT3 installs performance-health systems inside the orgs that depend on the people running them. Built for staff and faculty — measured by output, not optics.
            </motion.p>

            <motion.div variants={fadeUp}>
              <a
                href={PARTNERSHIP_CTA}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2.5 bg-[#141416] hover:bg-[#c6a04a] hover:text-[#141416] text-[#f4efe6] font-sans-clean text-[12px] md:text-[13px] font-medium tracking-[0.18em] uppercase px-7 py-4 transition-all"
              >
                Book a Partnership Call
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* SIGNATURE BAND — backlit CT3 signage, full image (no crop) */}
      <section className="relative w-full bg-[#141416] overflow-hidden py-10 md:py-16">
        <div className="relative w-full max-w-[1400px] mx-auto px-6 md:px-10">
          <motion.img
            src="https://customer-assets.emergentagent.com/job_driven-12week/artifacts/6e48hpw4_0762C952-E703-45A1-BED6-946238B6C2DC.png"
            alt="CT3 Performance — Studio"
            loading="lazy"
            decoding="async"
            initial={{ scale: 1.03, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="block w-full h-auto editorial-photo"
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 md:mt-8 flex items-center gap-3"
          >
            <div className="h-px w-10 md:w-14 bg-[#c6a04a]" />
            <p className="font-sans-clean text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#c6a04a]">
              The Studio · Nashville
            </p>
          </motion.div>
        </div>
      </section>

      {/* WHY IT MATTERS — output metrics */}
      <section className="bg-[#f4efe6] py-24 md:py-32 px-6 md:px-10 border-t border-[#141416]/10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-12 mb-16 md:mb-24"
          >
            <div className="col-span-12 md:col-span-10 md:col-start-2">
              <p className="font-sans-clean text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#c6a04a] mb-6">
                Why It Matters
              </p>
              <h2
                className="font-editorial text-[2rem] sm:text-5xl md:text-6xl leading-[1.05] tracking-[-0.03em] text-[#141416]"
                style={{ fontVariationSettings: '"opsz" 144, "SOFT" 30, "wght" 380' }}
              >
                The team's health is the team's <span className="font-editorial-italic text-[#c6a04a]" style={{ fontVariationSettings: '"opsz" 144, "SOFT" 70' }}>output.</span>
              </h2>
            </div>
          </motion.div>

          <div className="grid grid-cols-12 gap-x-8 gap-y-14">
            {[
              { metric: 'Energy', body: 'Higher daily capacity across the team.' },
              { metric: 'Focus', body: 'Fewer foggy hours. Sharper decisions.' },
              { metric: 'Absences', body: 'Fewer sick days over the engagement.' },
              { metric: 'Retention', body: 'Investment in health signals investment in people.' },
              { metric: 'Culture', body: 'A standard your team can feel — and recruits can see.' }
            ].map((item, i) => (
              <motion.div
                key={item.metric}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="col-span-6 md:col-span-4 lg:col-span-2 space-y-4"
              >
                <p
                  className="font-editorial text-[1.5rem] md:text-3xl text-[#141416] tracking-[-0.02em]"
                  style={{ fontVariationSettings: '"opsz" 144, "SOFT" 35, "wght" 400' }}
                >
                  {item.metric}
                </p>
                <div className="h-px w-8 bg-[#c6a04a]" />
                <p className="font-sans-clean text-[13px] md:text-sm text-[#141416]/65 leading-[1.65]">
                  {item.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE DELIVER — dark editorial section */}
      <section className="bg-[#141416] text-[#f4efe6] py-28 md:py-40 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-12 mb-20 md:mb-28"
          >
            <div className="col-span-12 md:col-span-10 md:col-start-2">
              <p className="font-sans-clean text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#c6a04a] mb-6">
                What We Deliver
              </p>
              <h2
                className="font-editorial text-[2rem] sm:text-5xl md:text-6xl leading-[1.05] tracking-[-0.03em] text-[#f4efe6]"
                style={{ fontVariationSettings: '"opsz" 144, "SOFT" 30, "wght" 380' }}
              >
                Programming designed for busy people. <span className="font-editorial-italic text-[#c6a04a]" style={{ fontVariationSettings: '"opsz" 144, "SOFT" 70' }}>Built to roll out.</span>
              </h2>
            </div>
          </motion.div>

          <div className="grid grid-cols-12 gap-x-10 gap-y-14 md:gap-y-20">
            {[
              {
                title: 'Structured programming',
                body: 'Training, nutrition, and recovery protocols tuned to the team. Adjusted by data, not opinion.',
                cols: 'col-span-12 md:col-span-5 md:col-start-2'
              },
              {
                title: 'Weekly accountability',
                body: 'Check-ins and progress markers that keep participants on the rails when the calendar gets tight.',
                cols: 'col-span-12 md:col-span-5 md:col-start-7'
              },
              {
                title: 'Education sessions',
                body: 'Workshops on sleep, nutrition, recovery, and habit design — the foundations behind the work.',
                cols: 'col-span-12 md:col-span-5 md:col-start-2'
              },
              {
                title: '1:1 access for leadership',
                body: 'Optional individualized coaching for executives and key personnel who need it tuned to them.',
                cols: 'col-span-12 md:col-span-5 md:col-start-7'
              },
              {
                title: 'Simple rollout',
                body: 'Clean onboarding, minimal admin lift, a single point of contact.',
                cols: 'col-span-12 md:col-span-5 md:col-start-2'
              },
              {
                title: 'Measurable outcomes',
                body: 'Progress tracked against real health markers and reported back to stakeholders.',
                cols: 'col-span-12 md:col-span-5 md:col-start-7'
              }
            ].map((d, i) => (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 2) * 0.08, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className={d.cols}
              >
                <div className="h-px w-10 bg-[#c6a04a]/50 mb-6" />
                <h3
                  className="font-editorial text-[1.4rem] md:text-3xl text-[#f4efe6] mb-4 leading-[1.15] tracking-[-0.02em]"
                  style={{ fontVariationSettings: '"opsz" 144, "SOFT" 35, "wght" 400' }}
                >
                  {d.title}
                </h3>
                <p className="font-sans-clean text-[15px] text-[#f4efe6]/60 leading-[1.7] max-w-md">
                  {d.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FORMATS — light, simple list */}
      <section className="bg-[#f4efe6] py-28 md:py-40 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-12 mb-16 md:mb-24"
          >
            <div className="col-span-12 md:col-span-10 md:col-start-2">
              <p className="font-sans-clean text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#c6a04a] mb-6">
                Formats
              </p>
              <h2
                className="font-editorial text-[2rem] sm:text-5xl md:text-6xl leading-[1.05] tracking-[-0.03em] text-[#141416]"
                style={{ fontVariationSettings: '"opsz" 144, "SOFT" 30, "wght" 380' }}
              >
                Engagements <span className="font-editorial-italic text-[#c6a04a]" style={{ fontVariationSettings: '"opsz" 144, "SOFT" 70' }}>shaped</span> to your org.
              </h2>
            </div>
          </motion.div>

          <div className="grid grid-cols-12 gap-y-0 max-w-5xl mx-auto md:ml-[8.333%]">
            {[
              { no: '01', label: 'Cohort Programs', body: '12, 24, or 52-week structured group programs with progress checkpoints.' },
              { no: '02', label: 'Workshops & Seminars', body: 'On-site or remote sessions on performance health fundamentals.' },
              { no: '03', label: 'Leadership 1:1', body: 'Individualized coaching for executives and key personnel.' },
              { no: '04', label: 'Ongoing Membership', body: 'Programming, accountability, and education — year-round.' }
            ].map((f, i) => (
              <motion.div
                key={f.no}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="col-span-12 grid grid-cols-12 gap-x-8 py-8 md:py-10 border-b border-[#141416]/10 first:border-t first:border-[#141416]/10 items-baseline"
              >
                <p
                  className="col-span-2 md:col-span-1 font-editorial-italic text-[#c6a04a] text-base md:text-lg"
                  style={{ fontVariationSettings: '"opsz" 144, "SOFT" 70' }}
                >
                  {f.no}
                </p>
                <h3
                  className="col-span-10 md:col-span-5 font-editorial text-xl md:text-3xl text-[#141416] tracking-[-0.02em]"
                  style={{ fontVariationSettings: '"opsz" 144, "SOFT" 35, "wght" 400' }}
                >
                  {f.label}
                </h3>
                <p className="col-start-3 md:col-start-7 col-span-10 md:col-span-6 font-sans-clean text-[14px] md:text-base text-[#141416]/65 leading-[1.7] mt-2 md:mt-0">
                  {f.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STANDARD — dark pull quote */}
      <section className="bg-[#141416] text-[#f4efe6] py-28 md:py-40 px-6 md:px-10">
        <div className="max-w-6xl mx-auto grid grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="col-span-12 md:col-span-10 md:col-start-2"
          >
            <p className="font-sans-clean text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#c6a04a] mb-10">
              The Standard
            </p>
            <p
              className="font-editorial text-[1.75rem] sm:text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.2] tracking-[-0.02em] text-[#f4efe6]/95 mb-10"
              style={{ fontVariationSettings: '"opsz" 144, "SOFT" 40, "wght" 380' }}
            >
              "We don't sell a wellness benefit. We install a system that produces sharper, more durable people — <span className="font-editorial-italic text-[#c6a04a]" style={{ fontVariationSettings: '"opsz" 144, "SOFT" 70' }}>and measure it like we mean it.</span>"
            </p>
            <p className="font-sans-clean text-[12px] text-[#f4efe6]/55 uppercase tracking-[0.2em]">
              Clifton Tant — Founder
            </p>
          </motion.div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section id="partnership" className="bg-[#f4efe6] py-28 md:py-40 px-6 md:px-10 border-t border-[#141416]/10">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-12"
          >
            <div className="col-span-12 md:col-span-10">
              <h2
                className="font-editorial text-[2.25rem] sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.03em] text-[#141416] mb-10 md:mb-12"
                style={{ fontVariationSettings: '"opsz" 144, "SOFT" 30, "wght" 380' }}
              >
                Let's talk about what your team <span className="font-editorial-italic text-[#c6a04a]" style={{ fontVariationSettings: '"opsz" 144, "SOFT" 70' }}>actually needs.</span>
              </h2>
              <p className="font-sans-clean text-base md:text-lg text-[#141416]/65 max-w-xl mb-12 leading-[1.7]">
                A 20-minute partnership call. No deck. No commitment.
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                <a
                  href={PARTNERSHIP_CTA}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2.5 bg-[#141416] hover:bg-[#c6a04a] hover:text-[#141416] text-[#f4efe6] font-sans-clean text-[12px] md:text-[13px] font-medium tracking-[0.18em] uppercase px-7 py-4 transition-all"
                >
                  Book a Partnership Call
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
                </a>
                <Link
                  to="/"
                  className="font-sans-clean text-[12px] text-[#141416]/55 hover:text-[#c6a04a] uppercase tracking-[0.2em] transition-colors"
                >
                  ← Home
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <EditorialFooter light />
    </div>
  );
};

export default Organizations;
